/**
 * Service Worker Registration — handles registration, update detection,
 * and forced activation for CW POS offline caching.
 *
 * When a new SW version is detected:
 * - If no active transaction (no cart items), auto-reload to pick up new code
 * - If transaction in progress, defer reload until cart is empty
 *
 * Update detection:
 * - 5-minute polling via registration.update()
 * - Startup freshness check: compares running version against sw-manifest.json
 * - updateViaCache: "none" ensures browser always fetches sw.js from network
 */

const UPDATE_POLL_INTERVAL = 5 * 60 * 1000;

let pendingUpdate = false;

function hasActiveTransaction() {
  try {
    const path = window.location.pathname;
    if (path === "/login" || path === "/sync" || path === "/open-till" || path === "/open-till-component") {
      return true;
    }

    const cartObj = localStorage.getItem("cartObj");
    if (!cartObj) return false;
    const cart = JSON.parse(cartObj);
    return Array.isArray(cart?.items) && cart.items.length > 0;
  } catch {
    return false;
  }
}

function applyUpdate(registration) {
  if (registration?.waiting) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
}

function getRunningVersion() {
  try {
    return import.meta.env.VITE_APP_VERSION || null;
  } catch {
    return null;
  }
}

async function checkVersionFreshness(registration) {
  try {
    const runningVersion = getRunningVersion();
    if (!runningVersion) return;

    const res = await fetch("./sw-manifest.json?_t=" + Date.now(), { cache: "no-store" });
    if (!res.ok) return;
    const manifest = await res.json();
    if (manifest.version && manifest.version !== runningVersion) {
      registration.update().catch(() => {});
    }
  } catch {
    // Network error — skip freshness check
  }
}

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  if (import.meta.env.DEV) return;

  window.addEventListener("load", async () => {
    try {
      const registration = await navigator.serviceWorker.register("./sw.js", {
        scope: "./",
        updateViaCache: "none",
      });

      checkVersionFreshness(registration);

      setInterval(() => {
        registration.update().catch(() => {});
      }, UPDATE_POLL_INTERVAL);

      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {

            if (!hasActiveTransaction()) {
              applyUpdate(registration);
            } else {
              pendingUpdate = true;
            }
          }
        });
      });

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        const path = window.location.pathname;
        if (path === "/login" || path === "/sync" || path === "/open-till" || path === "/open-till-component") {
          pendingUpdate = false;
          return;
        }
        window.location.reload();
      });

      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "SW_UPDATED") {
        }
      });
    } catch (err) {
      console.warn("[SW] Registration failed:", err.message);
    }
  });
}

export function checkPendingSwUpdate() {
  if (!pendingUpdate) return;
  if (hasActiveTransaction()) return;

  pendingUpdate = false;
  navigator.serviceWorker?.getRegistration().then((reg) => {
    if (reg?.waiting) {
      applyUpdate(reg);
    }
  });
}
